import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  FC,
  useCallback,
  useMemo
} from 'react'
import {
  BouncingLoader,
  Button,
  Checkbox,
  CloseIcon,
  Modal,
  Tooltip
} from 'examkrackers-components'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useWindowDimensions } from 'hooks/useWindowDimensions'
import QuizQuestionView from './QuizQuestionView'
import QuizResultsView from './QuizResultsView'
import QuizProgressHeader from './QuizProgressHeader'
import styled from 'styled-components'
import fail from 'assets/fail.svg'
import warning from 'assets/warning.svg'
import {
  getAnsweredQuestionCount,
  getConceptSummary,
  getCorrectFirstAttemptCount,
  getCurrentQuestionNo,
  getPercentageScore,
  getProgressStatuses,
  getResultFeedbackTranslationKey,
  getResultsRows
} from './quiz.helpers'
import {
  generateAiSpacedRepetitionQuiz,
  submitAiSpacedRepetitionQuiz,
  updateSrqRedDotsVisibility
} from 'services/BooksService'
import { mapAiSpacedRepetitionQuizResponseToQuestions } from './mapAiSpacedRepetitionQuizResponse'
import { getErrorMessage, getErrorName } from 'utils/errors'
import {
  CloseIconWrapper,
  ErrorContainer,
  ErrorText,
  ErrorTitle,
  HeaderCloseButton,
  HeaderDivider,
  HeaderTopRow,
  HeadingRow,
  HeadingTitleClip,
  HeadingTooltipTrigger,
  LoadingContainer,
  LoadingTitle,
  QuizBody,
  QuizHeader,
  QuizHeading,
  QuizModalContainer,
  quizModalDesktopWidthPx,
  quizModalMobileMaxWidthPx,
  QuizNextButton,
  QuizNextFooter,
  QuizQuestionContent,
  QuizQuestionScreen,
  AnswerChoicesInfoModalInner,
  AnswerChoicesInfoCloseButton,
  AnswerChoicesInfoIcon,
  AnswerChoicesInfoTitle,
  AnswerChoicesInfoBody,
  AnswerChoicesInfoActions,
  AnswerChoicesInfoDontShowAgain,
  AnswerChoicesInfoDontShowAgainText
} from './QuizModal.styles'
import {
  AiSpacedRepetitionRedDotsResponse,
  AiSpacedRepetitionSubmitResponse,
  getNextInQueueSubchapterId,
  QuizQuestion,
  QuizQuestionAnswer,
  QuizScreenState
} from './quiz.types'
import { getIsIosTouchDevice } from './quizIosTouchDevice'
import { AiSpacedRepetitionQuizSubmitPayload } from 'types'
import { getSrqTimezone } from 'modules/quizDiagnostics/utils/getSrqApiParams'
import {
  getSharedSrqStatus,
  patchSharedSrqStatus
} from 'modules/quizDiagnostics/utils/srqStatusStore'
import {
  getChapterAveragesPath,
  getSectionAveragesPath,
  isAveragesPath
} from 'modules/quizDiagnostics/utils/quizDiagnosticsParams'
import {
  markSrqAnswerChoicesModalSeen
} from 'services/AuthService'
import { refreshStudentProfile } from './useOpenSrqQuizFlow'

const EMPTY_REVEALED_OPTION_IDS: string[] = []

const answerChoicesInfoModalStyle = {
  overlay: { zIndex: 10000000 },
  content: {
    zIndex: 10000001,
    boxSizing: 'border-box' as const,
    minWidth: 'min(532px, calc(100vw - 32px))',
    width: 'min(532px, calc(100vw - 32px))',
    maxWidth: 'calc(100vw - 16px)',
    padding: '36px 40px 28px',
    marginLeft: 'auto',
    marginRight: 'auto',
    border: '2px solid #F8961A',
    borderRadius: '8px',
    boxShadow: '0px 4px 12px 0px #0000001A',
    background: '#fff',
    position: 'relative' as const
  }
}

const QuizModal: FC<{
  isOpen: boolean
  onClose: () => void
  onQuizCompleted?: () => void
  studentBookSubchapterId: string
  studentBookChapterId?: string
  bookAbbreviation?: string
  chapterOrder?: number
  sectionOrder?: string
  sectionTitle?: string
}> = ({
  isOpen,
  onClose,
  onQuizCompleted,
  studentBookSubchapterId,
  studentBookChapterId = '',
  bookAbbreviation = '',
  chapterOrder = 1,
  sectionOrder = '1',
  sectionTitle = ''
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { push, replace, location } = useHistory()
  const { width } = useWindowDimensions()
  const isMobileLayout = width <= quizModalMobileMaxWidthPx

  const modalStyle = useMemo(() => {
    const contentBase = {
      padding: 0,
      border: 'none',
      background: 'transparent',
      overflow: 'hidden'
    }

    if (!isMobileLayout) {
      return {
        content: {
          ...contentBase,
          width: `${quizModalDesktopWidthPx}px`,
          maxWidth: `${quizModalDesktopWidthPx}px`
        }
      }
    }

    return {
      overlay: {
        height: '100dvh',
        minHeight: '100dvh',
        padding: 0,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch'
      },
      content: {
        ...contentBase,
        position: 'fixed' as const,
        inset: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: '100%',
        height: '100dvh',
        maxHeight: '100dvh',
        minHeight: '100dvh',
        margin: 0,
        transform: 'none',
        display: 'flex',
        flexDirection: 'column' as const
      }
    }
  }, [isMobileLayout])

  const [screenState, setScreenState] = useState<QuizScreenState>('loading')
  const [quizId, setQuizId] = useState('')
  const [submitQuizResponse, setSubmitQuizResponse] =
    useState<AiSpacedRepetitionSubmitResponse | null>(null)
  const [courseQuizzesDueToday, setCourseQuizzesDueToday] = useState(0)
  const [isSrqRedDotsVisible, setIsSrqRedDotsVisible] = useState(true)
  const [isUpdatingSrqRedDots, setIsUpdatingSrqRedDots] = useState(false)
  const [includeInAlgorithmSectionLabel, setIncludeInAlgorithmSectionLabel] =
    useState(`${bookAbbreviation} ${chapterOrder}.${sectionOrder}`.trim())
  const activeSubchapterIdRef = useRef(studentBookSubchapterId)
  const activeChapterIdRef = useRef(studentBookChapterId)
  const [activeBookAbbreviation, setActiveBookAbbreviation] =
    useState(bookAbbreviation)
  const [heading, setHeading] = useState('')
  const [questions, setQuestions] = useState<QuizQuestion[]>([])
  const [answers, setAnswers] = useState<Record<string, QuizQuestionAnswer>>({})
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [
    revealedAnswersByQuestionId,
    setRevealedAnswersByQuestionId
  ] = useState<Record<string, boolean>>({})
  const [
    revealedOptionIdsByQuestionId,
    setRevealedOptionIdsByQuestionId
  ] = useState<Record<string, string[]>>({})
  const [isAnswerChoicesInfoOpen, setIsAnswerChoicesInfoOpen] = useState(false)
  const [dontShowAnswerChoicesInfoAgain, setDontShowAnswerChoicesInfoAgain] =
    useState(false)
  const [errorCode, setErrorCode] = useState('')
  const [errorDetail, setErrorDetail] = useState('')
  const quizGenerationRef = useRef(0)
  const isSubmittingQuizRef = useRef(false)

  const headingRef = useRef<HTMLDivElement | null>(null)
  const [isHeadingTruncated, setIsHeadingTruncated] = useState(false)
  const headingMeasureFrameRef = useRef<number | null>(null)

  const measureHeadingOverflow = useCallback(() => {
    if (screenState === 'loading' || screenState === 'error') {
      setIsHeadingTruncated(false)
      return
    }

    const el = headingRef.current
    if (!el) {
      setIsHeadingTruncated(false)
      return
    }

    const clientW = el.clientWidth
    const scrollW = el.scrollWidth
    let isOverflowing = clientW > 0 && scrollW > clientW

    if (getIsIosTouchDevice() && clientW > 0 && !isOverflowing) {
      try {
        const range = document.createRange()
        range.selectNodeContents(el)
        const textWidth = range.getBoundingClientRect().width
        isOverflowing = textWidth > clientW + 1
      } catch {
        isOverflowing = scrollW > clientW
      }
    }

    setIsHeadingTruncated(isOverflowing)
  }, [screenState])

  useLayoutEffect(() => {
    if (headingMeasureFrameRef.current) {
      window.cancelAnimationFrame(headingMeasureFrameRef.current)
      headingMeasureFrameRef.current = null
    }

    let cancelled = false
    let innerRaf = 0

    const runMeasure = () => {
      if (!cancelled) {
        measureHeadingOverflow()
      }
    }

    const ios = getIsIosTouchDevice()
    if (!ios) {
      headingMeasureFrameRef.current = window.requestAnimationFrame(runMeasure)
    } else {
      headingMeasureFrameRef.current = window.requestAnimationFrame(() => {
        if (cancelled) {
          return
        }
        innerRaf = window.requestAnimationFrame(() => {
          if (cancelled) {
            return
          }
          runMeasure()
          if (typeof document !== 'undefined' && document.fonts?.ready) {
            document.fonts.ready
              .then(() => {
                if (!cancelled) {
                  runMeasure()
                }
              })
              .catch(() => undefined)
          }
        })
      })
    }

    return () => {
      cancelled = true
      if (headingMeasureFrameRef.current != null) {
        window.cancelAnimationFrame(headingMeasureFrameRef.current)
        headingMeasureFrameRef.current = null
      }
      if (innerRaf !== 0) {
        window.cancelAnimationFrame(innerRaf)
      }
    }
  }, [heading, measureHeadingOverflow, width, isOpen, screenState])

  useEffect(() => {
    if (
      !headingRef.current ||
      screenState === 'loading' ||
      screenState === 'error'
    ) {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      measureHeadingOverflow()
    })

    resizeObserver.observe(headingRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [heading, isOpen, measureHeadingOverflow, screenState])

  const resetLocalQuizState = useCallback(() => {
    setQuizId('')
    setSubmitQuizResponse(null)
    setCourseQuizzesDueToday(0)
    setIsSrqRedDotsVisible(true)
    setIsUpdatingSrqRedDots(false)
    setQuestions([])
    setAnswers({})
    setCurrentQuestionIndex(0)
    setRevealedAnswersByQuestionId({})
    setRevealedOptionIdsByQuestionId({})
    setIsAnswerChoicesInfoOpen(false)
    setDontShowAnswerChoicesInfoAgain(false)
    setErrorCode('')
    setErrorDetail('')
    isSubmittingQuizRef.current = false
  }, [])

  const handleClose = useCallback(() => {
    resetLocalQuizState()
    onClose()
  }, [onClose, resetLocalQuizState])
  const noopHandleClose = useCallback(() => undefined, [])

  const handleGenerateQuiz = useCallback(
    async (subchapterIdOverride?: string) => {
      resetLocalQuizState()
      setScreenState('loading')

      const generationId = quizGenerationRef.current + 1
      quizGenerationRef.current = generationId

      const targetSubchapterId =
        typeof subchapterIdOverride === 'string' && subchapterIdOverride
          ? subchapterIdOverride
          : activeSubchapterIdRef.current

      if (!targetSubchapterId) {
        if (quizGenerationRef.current !== generationId) {
          return
        }

        setErrorDetail('')
        setErrorCode('MISSING_SECTION')
        setScreenState('error')
        return
      }

      try {
        const response = await generateAiSpacedRepetitionQuiz(
          targetSubchapterId
        )
        if (quizGenerationRef.current !== generationId) {
          return
        }

        const nextQuiz = mapAiSpacedRepetitionQuizResponseToQuestions(
          response?.data?.data
        )
        const nextQuizId = String(
          response?.data?.data?.quiz_id ||
            response?.data?.data?.quizId ||
            response?.data?.data?.id ||
            response?.data?.quiz_id ||
            response?.data?.quizId ||
            response?.data?.id ||
            ''
        )

        if (!nextQuiz.length) {
          throw new Error('EMPTY_QUIZ')
        }
        const responseTag = String(response?.data?.tag || '').trim()
        const responseChapterOrder = response?.data?.chapter_order
        const responseSectionOrder = response?.data?.section_order
        const headingData = `${response?.data?.tag} ${response?.data?.chapter_order}.${response?.data?.section_order} ${response?.data?.section_title}`
        setHeading(headingData || '')
        if (responseTag && responseChapterOrder != null && responseSectionOrder != null) {
          setIncludeInAlgorithmSectionLabel(
            `${responseTag} ${responseChapterOrder}.${responseSectionOrder}`.trim()
          )
        }
        setQuizId(nextQuizId)
        setQuestions(nextQuiz)
        setScreenState('question')
      } catch (error) {
        if (quizGenerationRef.current !== generationId) {
          return
        }

        const err = error as Error
        setErrorDetail(getErrorMessage(err) || '')
        setErrorCode(
          getErrorName(err) || Math.random().toString(36).slice(2, 10)
        )
        setScreenState('error')
      }
    },
    [resetLocalQuizState]
  )

  const handleStartNextInQueue = useCallback(() => {
    const nextInQueue = submitQuizResponse?.next_in_queue
    const nextSubchapterId = nextInQueue?.student_book_subchapter_id ?? ''

    if (!nextSubchapterId) {
      return
    }

    const nextBookAbbreviation = nextInQueue?.tag || activeBookAbbreviation

    activeSubchapterIdRef.current = nextSubchapterId
    if (nextInQueue?.student_book_chapter_id) {
      activeChapterIdRef.current = nextInQueue.student_book_chapter_id
    }
    setActiveBookAbbreviation(nextBookAbbreviation)
    handleGenerateQuiz(nextSubchapterId)
  }, [
    activeBookAbbreviation,
    handleGenerateQuiz,
    submitQuizResponse?.next_in_queue
  ])

  const handleSeeQuizDiagnostics = useCallback(() => {
    const chapterId = activeChapterIdRef.current || studentBookChapterId
    const target = chapterId
      ? getSectionAveragesPath(chapterId)
      : getChapterAveragesPath()

    onClose()

    if (isAveragesPath(location.pathname)) {
      replace(target)
      return
    }

    push(target)
  }, [location.pathname, onClose, push, replace, studentBookChapterId])

  useEffect(() => {
    if (!isOpen) {
      quizGenerationRef.current += 1
      resetLocalQuizState()
      setScreenState('loading')
      return
    }

    activeSubchapterIdRef.current = studentBookSubchapterId
    activeChapterIdRef.current = studentBookChapterId
    setActiveBookAbbreviation(bookAbbreviation)
    setIncludeInAlgorithmSectionLabel(
      `${bookAbbreviation} ${chapterOrder}.${sectionOrder}`.trim()
    )
    handleGenerateQuiz(studentBookSubchapterId)

    return () => {
      quizGenerationRef.current += 1
    }
  }, [
    bookAbbreviation,
    chapterOrder,
    handleGenerateQuiz,
    isOpen,
    resetLocalQuizState,
    sectionOrder,
    sectionTitle,
    studentBookChapterId,
    studentBookSubchapterId
  ])

  const totalQuestions = questions.length
  const answeredCount = getAnsweredQuestionCount(answers)
  const correctCount = getCorrectFirstAttemptCount(answers)
  const progressStatuses = getProgressStatuses(questions, answers)
  const currentPercentage = getPercentageScore(correctCount, answeredCount)
  const finalPercentage = getPercentageScore(correctCount, totalQuestions)
  const resultsRows = getResultsRows(questions, answers)
  const conceptSummary = getConceptSummary(questions, answers)
  const hasSubmitQuizResponse = Boolean(submitQuizResponse)
  const nextInQueueSubchapterId = getNextInQueueSubchapterId(submitQuizResponse)
  const hasNextInQueue = Boolean(nextInQueueSubchapterId)
  const rawDaysUntilNextQuiz = submitQuizResponse?.days_until_next_quiz
  const hasDaysUntilNextQuiz =
    typeof rawDaysUntilNextQuiz === 'number' &&
    Number.isFinite(rawDaysUntilNextQuiz)
  const daysUntilNextQuiz = hasDaysUntilNextQuiz ? rawDaysUntilNextQuiz : null
  const isNextQuizInDays =
    hasDaysUntilNextQuiz && (daysUntilNextQuiz as number) > 1
  const currentQuestion = questions[currentQuestionIndex]
  const selectedOptionId = currentQuestion
    ? answers[currentQuestion.id]?.selectedOptionId || null
    : null
  const revealedOptionIds = currentQuestion
    ? revealedOptionIdsByQuestionId[currentQuestion.id] ||
      EMPTY_REVEALED_OPTION_IDS
    : EMPTY_REVEALED_OPTION_IDS
  const isCurrentQuestionAnswersVisible = currentQuestion
    ? Boolean(revealedAnswersByQuestionId[currentQuestion.id])
    : false
  const isLastQuestion =
    totalQuestions > 0 && currentQuestionIndex === totalQuestions - 1
  const hasSelectedAnswer = Boolean(selectedOptionId)
  const continueButtonLabel = isLastQuestion
    ? t('aiSpaceRepetition.complete')
    : t('aiSpaceRepetition.next')

  const currentLabel =
    screenState === 'results'
      ? `${totalQuestions} / ${totalQuestions}`
      : `${getCurrentQuestionNo(
          currentQuestionIndex,
          totalQuestions
        )} / ${totalQuestions}`
  const percentageLabel =
    screenState === 'results' ? `${finalPercentage}%` : `${currentPercentage}%`

  const handleRevealCurrentQuestionAnswers = () => {
    if (!currentQuestion) {
      return
    }

    setRevealedAnswersByQuestionId(previous => ({
      ...previous,
      [currentQuestion.id]: true
    }))
  }

  const handleShowAnswerChoices = async () => {
    if (!currentQuestion || isCurrentQuestionAnswersVisible) {
      return
    }

    try {
      const profile = await refreshStudentProfile(dispatch)

      if (profile.srq_answer_choices_modal_seen) {
        handleRevealCurrentQuestionAnswers()
        return
      }
    } catch {
      // Fall through to the info modal when profile cannot be loaded.
    }

    setDontShowAnswerChoicesInfoAgain(false)
    setIsAnswerChoicesInfoOpen(true)
  }

  const handleCloseAnswerChoicesInfo = () => {
    setIsAnswerChoicesInfoOpen(false)
  }

  const handleDontShowAgainChange = () => {
    setDontShowAnswerChoicesInfoAgain(previous => !previous)
  }

  const handleDontShowAgainLabelKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    handleDontShowAgainChange()
  }

  const handleConfirmShowAnswerChoices = async () => {
    if (dontShowAnswerChoicesInfoAgain) {
      try {
        await markSrqAnswerChoicesModalSeen()
        await refreshStudentProfile(dispatch)
      } catch {
        // Continue revealing answers even if preference save fails.
      }
    }

    handleRevealCurrentQuestionAnswers()
    setIsAnswerChoicesInfoOpen(false)
  }

  const handleSelectOption = (optionId: string) => {
    if (
      !currentQuestion ||
      screenState !== 'question' ||
      !isCurrentQuestionAnswersVisible
    ) {
      return
    }

    const existingAnswer = answers[currentQuestion.id]

    // After the first attempt, extra clicks only reveal more explanations.
    if (existingAnswer) {
      setRevealedOptionIdsByQuestionId(previous => {
        const currentIds = previous[currentQuestion.id] || []
        if (currentIds.includes(optionId)) {
          return previous
        }

        return {
          ...previous,
          [currentQuestion.id]: [...currentIds, optionId]
        }
      })
      return
    }

    const isCorrect = optionId === currentQuestion.correctOptionId
    const nextAnswer: QuizQuestionAnswer = {
      questionId: currentQuestion.id,
      firstSelectedOptionId: optionId,
      selectedOptionId: optionId,
      firstAttemptCorrect: isCorrect,
      isResolved: true
    }

    setAnswers(previous => ({
      ...previous,
      [currentQuestion.id]: nextAnswer
    }))
    setRevealedOptionIdsByQuestionId(previous => ({
      ...previous,
      [currentQuestion.id]: isCorrect
        ? [optionId]
        : Array.from(new Set([optionId, currentQuestion.correctOptionId]))
    }))
  }

  const applySrqRedDotsResponse = useCallback(
    (
      response: unknown,
      fallbackVisible: boolean
    ): { visible: boolean; dueToday: number | null } => {
      const typedResponse = response as {
        data?:
          | { data?: AiSpacedRepetitionRedDotsResponse }
          | AiSpacedRepetitionRedDotsResponse
      }

      const responseData =
        (typedResponse.data as { data?: AiSpacedRepetitionRedDotsResponse })
          ?.data ||
        (typedResponse.data as AiSpacedRepetitionRedDotsResponse) ||
        (typedResponse as AiSpacedRepetitionRedDotsResponse) ||
        null

      const visible = responseData?.srq_red_dots_visible ?? fallbackVisible
      setIsSrqRedDotsVisible(visible)

      const dueTodayFromRedDots = Number(responseData?.course_quizzes_due_today)
      const hasDueTodayFromRedDots = Number.isFinite(dueTodayFromRedDots)

      if (hasDueTodayFromRedDots) {
        setCourseQuizzesDueToday(dueTodayFromRedDots)
        patchSharedSrqStatus({
          quizzesDueToday: dueTodayFromRedDots,
          ...(typeof responseData?.course_srq_red_dots_visible === 'boolean'
            ? { srqRedDotsVisible: responseData.course_srq_red_dots_visible }
            : {})
        })
        return { visible, dueToday: dueTodayFromRedDots }
      }

      if (typeof responseData?.course_srq_red_dots_visible === 'boolean') {
        patchSharedSrqStatus({
          srqRedDotsVisible: responseData.course_srq_red_dots_visible
        })
      }

      return { visible, dueToday: null }
    },
    []
  )

  const refreshSrqRedDotsAfterSubmit = useCallback(
    async (subchapterId: string, visible: boolean) => {
      try {
        const response = await updateSrqRedDotsVisibility(subchapterId, {
          visible,
          timezone: getSrqTimezone()
        })
        applySrqRedDotsResponse(response, visible)
      } catch (error) {
        console.error('Failed to refresh SRQ red dots after submit', error)
        setCourseQuizzesDueToday(getSharedSrqStatus().quizzesDueToday)
      }
    },
    [applySrqRedDotsResponse]
  )

  const handleSubmitQuizAndShowResults = async () => {
    if (isSubmittingQuizRef.current) {
      return
    }

    const submitPayload: AiSpacedRepetitionQuizSubmitPayload = {
      quiz_id: quizId,
      answers: Object.values(answers)
        .filter(answer => Boolean(answer.firstSelectedOptionId))
        .map(answer => ({
          question_id: answer.questionId,
          selected_option_id: answer.firstSelectedOptionId
        })),
      timezone: getSrqTimezone()
    }

    if (
      activeSubchapterIdRef.current &&
      submitPayload.quiz_id &&
      !hasSubmitQuizResponse
    ) {
      isSubmittingQuizRef.current = true

      try {
        const response = await submitAiSpacedRepetitionQuiz(
          activeSubchapterIdRef.current,
          submitPayload
        )
        const submitResponse = response as {
          data?:
            | { data?: AiSpacedRepetitionSubmitResponse }
            | AiSpacedRepetitionSubmitResponse
        }
        const responseData =
          (
            submitResponse.data as {
              data?: AiSpacedRepetitionSubmitResponse
            }
          )?.data ||
          (submitResponse.data as AiSpacedRepetitionSubmitResponse) ||
          null
        setSubmitQuizResponse(responseData)

        const sectionVisible = responseData?.srq_red_dots_visible ?? true
        setIsSrqRedDotsVisible(sectionVisible)

        // Due count can change after submit — refresh from srq-red-dots API.
        await refreshSrqRedDotsAfterSubmit(
          activeSubchapterIdRef.current,
          sectionVisible
        )

        onQuizCompleted?.()
      } catch (error) {
        console.error('Failed to submit spaced repetition quiz', error)
      } finally {
        isSubmittingQuizRef.current = false
      }
    }

    setScreenState('results')
  }

  const handleIncludeInAlgorithmChange = useCallback(async () => {
    const subchapterId = activeSubchapterIdRef.current
    if (!subchapterId || isUpdatingSrqRedDots) {
      return
    }

    const previousVisible = isSrqRedDotsVisible
    const previousDueTodayCount = courseQuizzesDueToday
    const nextVisible = !previousVisible

    setIsSrqRedDotsVisible(nextVisible)
    setIsUpdatingSrqRedDots(true)

    try {
      const response = await updateSrqRedDotsVisibility(subchapterId, {
        visible: nextVisible,
        timezone: getSrqTimezone()
      })

      applySrqRedDotsResponse(response, nextVisible)
      onQuizCompleted?.()
    } catch (error) {
      console.error('Failed to update SRQ red dots visibility', error)
      setIsSrqRedDotsVisible(previousVisible)
      setCourseQuizzesDueToday(previousDueTodayCount)
    } finally {
      setIsUpdatingSrqRedDots(false)
    }
  }, [
    applySrqRedDotsResponse,
    courseQuizzesDueToday,
    isSrqRedDotsVisible,
    isUpdatingSrqRedDots,
    onQuizCompleted
  ])

  const canGoBack =
    (screenState === 'results' && totalQuestions > 0) ||
    (screenState === 'question' && currentQuestionIndex > 0)
  const canGoForward = screenState === 'question' && hasSelectedAnswer

  const handleGoBack = () => {
    if (!canGoBack) {
      return
    }

    if (screenState === 'results') {
      setCurrentQuestionIndex(totalQuestions - 1)
      setScreenState('question')
      return
    }

    setCurrentQuestionIndex(previous => previous - 1)
  }

  const handleGoForward = () => {
    if (!canGoForward) {
      return
    }

    if (isLastQuestion) {
      handleSubmitQuizAndShowResults()
      return
    }

    setCurrentQuestionIndex(previous => previous + 1)
  }

  const handleContinue = () => {
    handleGoForward()
  }

  return (
    <>
      <Modal
        data-testid='SpacedRepetitionSectionQuiz-Modal'
        open={isOpen}
        handleClose={noopHandleClose}
        blank
        fullscreen={isMobileLayout}
        style={modalStyle}
      >
        <QuizModalContainer>
          <QuizHeader>
            <CloseIconWrapper>
              <HeaderCloseButton
                type='button'
                aria-label={t('aiSpaceRepetition.closeQuiz')}
                onClick={handleClose}
              >
                <CloseIcon />
              </HeaderCloseButton>
            </CloseIconWrapper>
            {screenState !== 'loading' && screenState !== 'error' && (
              <HeaderTopRow>
                <QuizProgressHeader
                  currentLabel={currentLabel}
                  percentageLabel={percentageLabel}
                  progressStatuses={progressStatuses}
                  variant='header'
                  showCorrectSuffix
                  showNavigation={
                    screenState === 'question' || screenState === 'results'
                  }
                  canGoBack={canGoBack}
                  canGoForward={canGoForward}
                  previousQuestionLabel={t('aiSpaceRepetition.previousQuestion')}
                  nextQuestionLabel={t('aiSpaceRepetition.nextQuestion')}
                  onGoBack={handleGoBack}
                  onGoForward={handleGoForward}
                />
              </HeaderTopRow>
            )}
            {screenState !== 'loading' && screenState !== 'error' && (
              <HeadingRow>
                <HeadingTooltipTrigger>
                  <HeadingTitleClip>
                    {isHeadingTruncated ? (
                      <Tooltip
                        id='ai-space-repetition-heading-tooltip-title'
                        tooltipContent={heading}
                      >
                        <QuizHeading ref={headingRef}>{heading}</QuizHeading>
                      </Tooltip>
                    ) : (
                      <QuizHeading ref={headingRef}>{heading}</QuizHeading>
                    )}
                  </HeadingTitleClip>
                </HeadingTooltipTrigger>
              </HeadingRow>
            )}
          </QuizHeader>
          <HeaderDivider />
          <QuizBody>
            {screenState === 'loading' && (
              <LoadingContainer>
                <LoadingTitle>
                  {t('aiSpaceRepetition.generatingQuiz')}
                </LoadingTitle>
                <StyledBouncingLoader />
              </LoadingContainer>
            )}

            {screenState === 'error' && (
              <ErrorContainer>
                <img src={fail} alt='fail' height={48} />
                <ErrorTitle>{t('aiSpaceRepetition.errorTitle')}</ErrorTitle>
                <ErrorText>
                  {errorDetail || t('aiSpaceRepetition.errorUnknown')}
                </ErrorText>
                <ErrorText>
                  {t('aiSpaceRepetition.errorCode')}: {errorCode}
                </ErrorText>
                <ErrorText>
                  {t('aiSpaceRepetition.errorDescription')} <br />{' '}
                  {t('aiSpaceRepetition.email')}
                </ErrorText>
              </ErrorContainer>
            )}

            {screenState === 'question' && currentQuestion && (
              <QuizQuestionScreen>
                <QuizQuestionContent>
                  <QuizQuestionView
                    question={currentQuestion}
                    selectedOptionId={selectedOptionId}
                    revealedOptionIds={revealedOptionIds}
                    isAnswersVisible={isCurrentQuestionAnswersVisible}
                    canToggleAnswersVisible={false}
                    onToggleAnswersVisible={() => undefined}
                    answersHeaderLabel={t('aiSpaceRepetition.answerChoices')}
                    answersHeaderCaret={isCurrentQuestionAnswersVisible}
                    showAnswerChoicesLabel={t(
                      'aiSpaceRepetition.showAnswerChoices'
                    )}
                    onShowAnswerChoices={handleShowAnswerChoices}
                    onSelectOption={handleSelectOption}
                  />
                </QuizQuestionContent>
                <QuizNextFooter>
                  <QuizNextButton
                    type='button'
                    color='secondary'
                    disabled={!hasSelectedAnswer}
                    onClick={handleContinue}
                  >
                    {continueButtonLabel}
                  </QuizNextButton>
                </QuizNextFooter>
              </QuizQuestionScreen>
            )}

            {screenState === 'results' && (
              <QuizResultsView
                scoreLabel={t('aiSpaceRepetition.score', {
                  correct: correctCount,
                  total: totalQuestions
                })}
                percentageText={t('aiSpaceRepetition.percentage', {
                  value: finalPercentage
                })}
                explanationTitle={t('aiSpaceRepetition.explanation')}
                correctAnswerLabel={t('aiSpaceRepetition.correctAnswer')}
                yourAnswerLabel={t('aiSpaceRepetition.yourAnswer')}
                explanationLabel={t('aiSpaceRepetition.explanationLabel')}
                correctText={t('aiSpaceRepetition.correct')}
                incorrectText={t('aiSpaceRepetition.incorrect')}
                rows={resultsRows}
                conceptSummary={conceptSummary}
                finalScoreLabel={t('aiSpaceRepetition.finalScore', {
                  value: `${correctCount}/${totalQuestions} (${finalPercentage}%)`
                })}
                strengthsLabel={t('aiSpaceRepetition.strongConcepts')}
                weaknessesLabel={t('aiSpaceRepetition.weakConcepts')}
                quizMeAgainLabel={t('aiSpaceRepetition.quizMeAgain')}
                nextQuizLabel={t('aiSpaceRepetition.nextQuiz')}
                takeMeAgainQuizLabel={t('aiSpaceRepetition.takeMeAgainQuiz')}
                onGenerateNewQuiz={() => handleGenerateQuiz()}
                onStartNextInQueue={handleStartNextInQueue}
                hasNextInQueue={hasNextInQueue}
                courseQuizzesDueToday={courseQuizzesDueToday}
                includeInAlgorithmChecked={isSrqRedDotsVisible}
                includeInAlgorithmSectionLabel={includeInAlgorithmSectionLabel}
                includeInAlgorithmDisabled={isUpdatingSrqRedDots}
                onIncludeInAlgorithmChange={handleIncludeInAlgorithmChange}
                finalPercentage={finalPercentage}
                feedbackLabel={t(
                  getResultFeedbackTranslationKey(finalPercentage)
                )}
                nextQuizSchedulePrefix={
                  !hasDaysUntilNextQuiz
                    ? ''
                    : isNextQuizInDays
                      ? t('aiSpaceRepetition.nextQuizInDaysPrefix')
                      : t('aiSpaceRepetition.nextQuizTomorrowPrefix')
                }
                nextQuizScheduleHighlight={
                  !hasDaysUntilNextQuiz
                    ? ''
                    : isNextQuizInDays
                      ? t('aiSpaceRepetition.nextQuizInDaysHighlight', {
                          days: daysUntilNextQuiz
                        })
                      : t('aiSpaceRepetition.nextQuizTomorrowHighlight')
                }
                seeQuizDiagnosticsLabel={t('aiSpaceRepetition.seeQuizDiagnostics')}
                onSeeQuizDiagnostics={handleSeeQuizDiagnostics}
              />
            )}
          </QuizBody>
        </QuizModalContainer>
      </Modal>
      <Modal
        data-testid='SpacedRepetitionAnswerChoicesInfo-Modal'
        open={isAnswerChoicesInfoOpen}
        handleClose={handleCloseAnswerChoicesInfo}
        blank
        style={answerChoicesInfoModalStyle}
      >
        <AnswerChoicesInfoModalInner>
          <AnswerChoicesInfoCloseButton
            type='button'
            aria-label={t('aiSpaceRepetition.closeInfoModal')}
            onClick={handleCloseAnswerChoicesInfo}
          >
            <CloseIcon />
          </AnswerChoicesInfoCloseButton>
          <AnswerChoicesInfoIcon src={warning} alt='' aria-hidden='true' />
          <AnswerChoicesInfoTitle>
            {t('aiSpaceRepetition.answerChoicesHiddenTitle')}
          </AnswerChoicesInfoTitle>
          <AnswerChoicesInfoBody>
            {t('aiSpaceRepetition.answerChoicesHiddenBody')}
          </AnswerChoicesInfoBody>
          <AnswerChoicesInfoActions>
            <Button
              type='button'
              color='secondary'
              size='small'
              onClick={handleConfirmShowAnswerChoices}
            >
              {t('aiSpaceRepetition.showAnswerChoices')}
            </Button>
          </AnswerChoicesInfoActions>
          <AnswerChoicesInfoDontShowAgain>
            <Checkbox
              name='dont-show-answer-choices-info-again'
              isSelected={dontShowAnswerChoicesInfoAgain}
              onChange={handleDontShowAgainChange}
            />
            <AnswerChoicesInfoDontShowAgainText
              role='button'
              tabIndex={0}
              aria-label={t('aiSpaceRepetition.dontShowAgain')}
              onClick={handleDontShowAgainChange}
              onKeyDown={handleDontShowAgainLabelKeyDown}
            >
              {t('aiSpaceRepetition.dontShowAgain')}
            </AnswerChoicesInfoDontShowAgainText>
          </AnswerChoicesInfoDontShowAgain>
        </AnswerChoicesInfoModalInner>
      </Modal>
    </>
  )
}

export default QuizModal

const StyledBouncingLoader = styled(BouncingLoader)`
  margin-top: 40px;
`
